import React, { useEffect, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';

import { AppPagination } from '@/__core/components';
import EmployeeDisplay from '@/__core/components/EmployeeDisplay';

import DataTableModule from '@/__core/layout/module/DataTableModule';

import { RequestMaterialListRow } from '../types';
import type { RequestMaterialStatusOption } from '../redux/RequestMaterialStatusCRUD';

interface RequestMaterialListProps {
  rows: RequestMaterialListRow[];
  statusOptions?: RequestMaterialStatusOption[];
  loading?: boolean;
  canDelete?: boolean;
  onSelectRow: (row: RequestMaterialListRow) => void;
  onDeleteSelected?: (ids: string[]) => Promise<void>;
  onCreatePurchaseOrder?: (ids: string[]) => Promise<void>;
  pagination?: {
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
}



const statusTextColor = (status: string): string => {
  switch (status) {
    case 'yeu_cau_vat_tu':
    case 'request':
      return '#d97706';
    case 'submitted':
      return '#2563eb';
    case 'approved':
      return '#16a34a';
    case 'canceled':
      return '#dc2626';
    default:
      return '#6b7280';
  }
};

export default function RequestMaterialList({
  rows,
  statusOptions = [],
  loading = false,
  canDelete = true,
  onSelectRow,
  onDeleteSelected,
  onCreatePurchaseOrder,
  pagination,
  onPageChange,
}: RequestMaterialListProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState('');
  const [applyingAction, setApplyingAction] = useState(false);
  const canBulkSelect = canDelete;

  useEffect(() => {
    const rowIds = new Set(rows.map((r) => r.id));
    setSelectedIds((current) => current.filter((id) => rowIds.has(id)));
  }, [rows]);

  const areAllRowsSelected = useMemo(() => rows.length > 0 && selectedIds.length === rows.length, [rows.length, selectedIds.length]);
  const areSomeRowsSelected = useMemo(() => selectedIds.length > 0 && selectedIds.length < rows.length, [rows.length, selectedIds.length]);

  const getStatusOption = (statusValue: string) => {
    return statusOptions.find((option) => option.id === statusValue || option.code === statusValue) || null;
  };

  const handleToggleRow = (id: string) => {
    if (!canBulkSelect) {
      return;
    }
    setSelectedIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
  };

  const handleToggleAll = () => {
    if (!canBulkSelect) {
      return;
    }
    if (areAllRowsSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(rows.map((r) => r.id));
  };

  const handleApplyBulkAction = async () => {
    if (!bulkAction || selectedIds.length === 0) return;
    try {
      setApplyingAction(true);
      if (bulkAction === 'delete') {
        if (!canDelete) {
          return;
        }
        await onDeleteSelected?.(selectedIds);
      }
      if (bulkAction === 'purchase_order') await onCreatePurchaseOrder?.(selectedIds);
      setSelectedIds([]);
      setBulkAction('');
    } catch (_error) {
      // Parent callback displays the detailed API error in the module snackbar.
    } finally {
      setApplyingAction(false);
    }
  };

  const columns = [
    {
      id: 'request_no',
      label: 'Mã',
      width: '140px',
      render: (_value: unknown, row: RequestMaterialListRow) => <Typography sx={{ fontWeight: 600 }}>{row.request_no}</Typography>,
    },
    {
      id: 'item_name',
      label: 'Tên vật tư',
      width: '240px',
      render: (_value: unknown, row: RequestMaterialListRow) => {
        const itemNames = Array.from(new Set((row.lines || []).map((line) => line.item_name).filter(Boolean) as string[]));
        if (!itemNames.length) {
          return '—';
        }

        if (itemNames.length === 1) {
          return itemNames[0];
        }

        return `${itemNames[0]} +${itemNames.length - 1}`;
      },
    },
  
    {
      id: 'requested_at',
      label: 'Ngày đề xuất',
      width: '170px',
    },
    {
      id: 'required_by',
      label: 'Ngày cần có',
      width: '170px',
    },
    {
      id: 'status',
      label: 'Trạng thái',
      width: '140px',
      render: (_value: unknown, row: RequestMaterialListRow) => {
        const statusOption = getStatusOption(row.status);
        const statusLabel = statusOption?.name || row.status;
        const statusColor = statusOption?.color || statusTextColor(row.status);
        return <Typography sx={{ color: statusColor, fontWeight: 600, fontSize: '0.875rem' }}>{statusLabel}</Typography>;
      },
    },
    {
      id: 'requested_by_name',
      label: 'Nhân viên đề xuất',
      width: '200px',
      render: (_value: unknown, row: RequestMaterialListRow) => (
        <EmployeeDisplay
          name={row.requested_by_name || undefined}
          employeeId={row.requested_by_id || undefined}
          fallbackText='—'
        />
      ),
    },
    {
      id: 'created_by',
      label: 'Người tạo',
      width: '200px',
      render: (_value: unknown, row: RequestMaterialListRow) => (
        <EmployeeDisplay
          name={row.created_by || undefined}
          employeeId={row.created_by || undefined}
          fallbackText='—'
        />
      ),
    },
  ];

  const totalCount = pagination?.count || rows.length;
  const currentPage = pagination?.page || 1;
  const pageSize = pagination?.pageSize || rows.length || DEFAULT_PAGE_SIZE;
  const totalPages = pagination?.totalPages || 1;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, height: 220 }}>
        <Typography variant='body1' color='text.secondary'>Chưa có phiếu yêu cầu vật tư</Typography>
        <Typography variant='body2' color='text.secondary'>Thử thay đổi bộ lọc hoặc tạo phiếu mới.</Typography>
      </Box>
    );
  }

  return (
    <>
      <DataTableModule
        data={rows}
        columns={columns}
        leftFilterWidth={0}
        onRowSelect={handleToggleRow}
        onSelectAll={handleToggleAll}
        onRowClick={(row) => onSelectRow(row as RequestMaterialListRow)}
        getRowId={(row) => (row as RequestMaterialListRow).id}
        isRowSelected={(row) => selectedIds.includes((row as RequestMaterialListRow).id)}
        areAllRowsSelected={areAllRowsSelected}
        areSomeRowsSelected={areSomeRowsSelected}
        showCheckbox={canBulkSelect}
      />
      {selectedIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            px: 2,
            py: 2,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            <Typography variant='body2' sx={{ color: '#374151' }}>
              Đã chọn {selectedIds.length} phiếu
            </Typography>
            <TextField
              select
              size='small'
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              disabled={applyingAction}
              sx={{ minWidth: 260 }}
            >
              <MenuItem value=''>Chọn action cho phiếu đã chọn</MenuItem>
              {canDelete ? <MenuItem value='delete'>Xóa</MenuItem> : null}
              <MenuItem value='purchase_order'>Lập phiếu mua</MenuItem>
            </TextField>
            <Button
              variant='contained'
              size='small'
              onClick={() => { void handleApplyBulkAction(); }}
              disabled={!bulkAction || applyingAction}
              sx={{ minWidth: 84, height: 32, px: 1.5 }}
            >
              {applyingAction ? 'Đang xử lý...' : 'Áp dụng'}
            </Button>
          </Box>
        </Box>
      )}
      <AppPagination
        count={totalCount}
        page={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        itemLabel='phiếu'
        onPageChange={onPageChange}
      />
    </>
  );
}
